/**
 * Login — inloggning med engångslänk (magic link).
 *
 * Det gamla lösenordsformuläret togs bort 2026-08-03. API:t accepterade
 * vilken e-post som helst med vilket lösenord som helst, och klienten
 * "loggade in" lokalt även när anropet misslyckades. Det såg ut som
 * inloggning men skyddade ingenting.
 *
 * Demoknappen finns kvar för Apples och Googles granskare.
 */
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthShell } from "../components/AuthShell";
import { DEMO_EMAIL, loginDemo, requestMagicLink, saveDemoSession } from "../lib/api";

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("demo") === "1" ? DEMO_EMAIL : "");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devLink, setDevLink] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const addr = email.trim();
    if (!addr) {
      setError("Fyll i din e-postadress.");
      return;
    }
    // demo@ ska ALDRIG gå via magic-link-flödet (det returnerar static profile
    // men ser ut som "kolla e-post"). Använd demoknappen / demo-endpoint.
    if (addr.toLowerCase() === DEMO_EMAIL.toLowerCase()) {
      await onDemo();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await requestMagicLink(addr);
      // Skydd: om API av misstag returnerar demo-token på en vanlig adress, logga inte in som demo.
      if (res.token && res.user?.isDemo) {
        setError("Den adressen är reserverad för granskar-demo. Använd knappen nedan.");
        return;
      }
      setSent(true);
      // Bara i testmiljö / allowlist med secret — i produktion mejlas länken.
      if (res.data?.devLink) setDevLink(res.data.devLink);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg.includes("rate_limited")) {
        setError("För många försök. Vänta en stund innan du begär en ny länk.");
      } else if (msg.includes("invalid_email")) {
        setError("Kontrollera e-postadressen.");
      } else {
        setError("Kunde inte skicka länken just nu. Försök igen om en stund.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function onDemo() {
    setBusy(true);
    setError(null);
    try {
      const res = await loginDemo();
      saveDemoSession(res.token, res.user);
      navigate("/");
    } catch {
      setError("Demoprofilen är inte tillgänglig just nu.");
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <AuthShell>
        <div className="auth-form">
          <h2 style={{ fontSize: 20, margin: "0 0 10px" }}>Kolla din e-post</h2>
          <p style={{ fontSize: 14, lineHeight: 1.5, color: "#4a4a4a" }}>
            Vi har skickat en engångslänk till <strong>{email.trim()}</strong> (om adressen
            är giltig). Nya konton skapas när du klickar första gången. Länken gäller i
            15 minuter och kan bara användas en gång.
          </p>
          {devLink && (
            <p style={{ fontSize: 12, marginTop: 12 }}>
              Testmiljö:{" "}
              <a className="auth-link" href={devLink}>
                öppna länken
              </a>
            </p>
          )}
          <button
            type="button"
            className="auth-btn auth-btn-secondary"
            style={{ marginTop: 18 }}
            onClick={() => {
              setSent(false);
              setDevLink(null);
            }}
          >
            Använd en annan adress
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <form className="auth-form" onSubmit={onSubmit} noValidate>
        <div className="auth-field">
          <label htmlFor="login-email">E-post</label>
          <input
            id="login-email"
            className="auth-input"
            type="email"
            autoComplete="email"
            placeholder="din@epost.se"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <p style={{ fontSize: 12.5, color: "#7d7979", margin: "0 0 12px", lineHeight: 1.45 }}>
          Vi skickar en inloggningslänk till din e-post. Inget lösenord att komma ihåg
          — och inget lösenord som kan läcka.
        </p>

        {error && <p style={{ color: "#B42318", fontSize: 13, margin: "0 0 8px" }}>{error}</p>}

        <button type="submit" className="auth-btn auth-btn-primary" disabled={busy}>
          {busy ? "Skickar…" : "Skicka inloggningslänk"}
        </button>

        <button
          type="button"
          className="auth-btn auth-btn-secondary"
          onClick={onDemo}
          disabled={busy}
          style={{ borderColor: "#D4AF37", color: "#7d5411" }}
        >
          Öppna demoprofil (reviewer)
        </button>

        <p
          style={{
            marginTop: 16,
            fontSize: 12,
            color: "#7d7979",
            lineHeight: 1.45,
            textAlign: "center",
          }}
        >
          Vill du bara titta?{" "}
          <Link className="auth-link" to="/demo">
            Öppna publik demo (inget konto)
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
